import { useCallback, useEffect, useState } from 'react';
import { useListCollaboration, type ListCollaboration } from './useListCollaboration';
import type { TrackerEntryDto } from './patientTrackingTypes';

/** Lower-cases the first letter of each top-level key (SignalR sends DTOs PascalCased). */
function camelizeKeys<T>(value: unknown): T {
  if (value == null || typeof value !== 'object') return value as T;
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    out[key.length > 0 ? key[0].toLowerCase() + key.slice(1) : key] = val;
  }
  return out as T;
}

export interface UseLiveRowsResult<T> extends ListCollaboration {
  rows: T[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  /** Optimistically apply a row locally (e.g. right after a successful save). */
  applyUpsert: (row: T) => void;
  applyRemove: (id: string) => void;
}

/** Live tracker board data passed from the page into grid/kanban views. */
export type TrackerBoardLiveData = Pick<
  UseLiveRowsResult<TrackerEntryDto>,
  | 'rows'
  | 'loading'
  | 'error'
  | 'reload'
  | 'applyUpsert'
  | 'rowEditors'
  | 'beginEditRow'
  | 'endEditRow'
  | 'setListEditing'
>;

/**
 * Loads a board's rows and keeps them live via the collaboration hub: RowUpserted replaces/adds by
 * id, RowRemoved drops by id. The local list stays authoritative for ordering; pushes only patch it.
 */
export interface UseLiveRowsOptions {
  /** When false, skips SignalR presence and live row pushes (read-only / log grids). */
  collaboration?: boolean;
}

export function useLiveRows<T extends { id: string }>(
  boardKey: string,
  loader: () => Promise<T[]>,
  options?: UseLiveRowsOptions
): UseLiveRowsResult<T> {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const applyUpsert = useCallback((row: T) => {
    if (!row?.id) return;
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.id === row.id);
      if (idx === -1) return [row, ...prev];
      const next = prev.slice();
      next[idx] = row;
      return next;
    });
  }, []);

  const applyRemove = useCallback((id: string) => {
    if (!id) return;
    setRows((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await loader());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, [loader]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const collaborationEnabled = options?.collaboration !== false;
  const collaboration = useListCollaboration({
    boardKey,
    enabled: collaborationEnabled,
    onRowUpserted: (change) => {
      if (change.row) applyUpsert(camelizeKeys<T>(change.row));
    },
    onRowRemoved: (change) => {
      if (change.rowId) applyRemove(change.rowId);
    }
  });

  return { rows, loading, error, reload, applyUpsert, applyRemove, ...collaboration };
}
